'use client'

import { useQueryClient } from '@tanstack/react-query'
import type { Review } from '@skop-app/lib-domain/types'
import { LabelWithRole, Ownership } from '@skop-app/lib-domain/types'
import { ReviewStatus, ReviewType } from '@skop-app/lib-types'
import { useTranslations } from 'next-intl'
import { v4 as uuid } from 'uuid'

import {
  useSubmitReviewInteraction,
  useUpsertReview,
} from '@/services/mutations'
import {
  executeReferenceUpsert,
  invalidateReferenceQueries,
  optimisticallySyncReferenceQueries,
} from '@/services/mutations/reference'
import type { CreateRefVariables } from '@/types/reference'
import { useReviewData, useUserContext } from '@/context'

import {
  buildCreationReviewInput,
  buildMentionTargets,
} from '@/utils/elementCreation.handler'
import type { EditorProperty } from '@/components/Grid/editors/SelectCreate/types'
import { listQueryKey } from '@/services/property/property.keys'
import type { IndexedReference } from '@/services/reference/reference.type'
import type { EditorOption } from '@/components/Grid/editors/SelectCreate/types'
import type { UpsertOptionVariables } from '@/services/property/option.mutation'
import {
  executeOptionUpsert,
  invalidateOptionQueries,
  optimisticallySyncOptionQueries,
} from '@/services/property/option.mutation'
import { prepareOptionCreation } from '@/services/property/option.creation'
import { resolveCreationReviewPolicy } from '@/features/reviews'
import { findOptionCreationReview } from '@/services/property/option.workflow'

type PostCommitSync = () => Promise<void> | void

export type CreateElementResult = {
  id: string
  value: string
  label: string
  status?: string
  target: 'option' | 'reference'
  createdReference?: IndexedReference
  afterCommit?: PostCommitSync
  nameOption?: Pick<EditorOption, 'id' | 'label' | 'status'> & {
    isNew: boolean
  }
}

type CreateElementProps = {
  inputValue: string
  property: EditorProperty
  field: string
  view?: 'references' | 'inventory'
  parentOptionId?: string
  ownership: Ownership | undefined
  byUserID: LabelWithRole[]
  reviewType: ReviewType
  isAdmin: boolean
  isSkopAdmin: boolean
  existingOption?: EditorOption
}

/**
 * Hook to create a new reference or an option in SelectCreateEditor
 * Exposes function to create a reference with it's first option or an option and create a review
 * @param {Object} params - Parameters for the createElement function
 * @param {string} params.inputValue - The label value of the new element
 * @param {Property} params.property - Contains metadata about the field
 * @param {string} params.field - The field of the new option, will also create a flag in grid cell
 * @param {Ownership} params.ownership - Who is creating the new element and what is the organization
 * @param {byUserID} params.byUserID - Array of team members that might be mentioned in the review message
 * @param {ReviewType} params.reviewType - The review type of the new element (reference or option)
 * @returns {createElement} - Function to create a reference or an option, returns the new element ID
 */
export const useCreateElement = () => {
  const t = useTranslations('Toolbar')
  const { teamReviewWorkflowPolicy } = useUserContext()

  const { mutateAsync: upsertReview } = useUpsertReview()
  const { submitInteraction } = useSubmitReviewInteraction()
  const { reviewsById } = useReviewData()
  const queryClient = useQueryClient()

  const submitReviewInteractionSafely = async ({
    message,
    logContext,
    ...params
  }: {
    message: string
    logContext: string
  } & Parameters<typeof submitInteraction>[0]) => {
    try {
      await submitInteraction({
        ...params,
        message,
      })
    } catch (error) {
      console.error(
        `Failed to create review interaction for ${logContext}`,
        error,
      )
    }
  }

  const syncOptionQueriesAfterCommit = (variables: UpsertOptionVariables) => {
    optimisticallySyncOptionQueries(queryClient, variables)
    void invalidateOptionQueries(queryClient, variables.queryKey)
  }

  const buildOptionAfterCommit = ({
    optionMutation,
  }: {
    optionMutation: UpsertOptionVariables
  }): PostCommitSync => {
    return () => {
      syncOptionQueriesAfterCommit(optionMutation)
    }
  }

  const createElement = async ({
    inputValue,
    property,
    field,
    view,
    parentOptionId,
    ownership,
    byUserID,
    reviewType,
    isAdmin,
    isSkopAdmin,
    existingOption,
  }: CreateElementProps): Promise<CreateElementResult> => {
    if (!ownership) {
      throw new Error('Missing ownership')
    }

    if (!property.key) {
      throw new Error('Missing property key')
    }

    const resolvedListId =
      typeof property.listId === 'string' && property.listId.length > 0
        ? property.listId
        : typeof property.value === 'string' && property.value.length > 0
          ? property.value
          : property.id
    const listOrganizationID =
      property.listOwnerOrganizationID ??
      property.organizationID ??
      process.env.NEXT_PUBLIC_SKOP_ORGID

    const isReferenceView = view === 'references'
    const needsReference =
      reviewType === ReviewType.REVIEW_REFERENCE && !isReferenceView
    if (
      existingOption &&
      (!needsReference ||
        !existingOption.id ||
        !['ACCEPTED', 'PENDING', 'RETURNED'].includes(
          existingOption.status ?? '',
        ) ||
        existingOption.isSelectable === false)
    ) {
      throw new Error('Existing name option is not selectable')
    }
    const optionID = existingOption?.id ?? uuid()
    const referenceID = needsReference ? uuid() : undefined
    const optionReviewID = uuid()
    const targetOrganizationID =
      ownership.organizationID ?? process.env.NEXT_PUBLIC_SKOP_ORGID ?? ''
    const preparedOption = existingOption
      ? undefined
      : prepareOptionCreation({
          elementID: optionID,
          reviewID: optionReviewID,
          inputValue,
          field,
          ownership,
          teammates: byUserID,
          source: 'manual',
          listOrganizationID,
          targetOrganizationID,
          isAdmin,
          isSkopAdmin,
          teamReviewWorkflowPolicy,
          parent: { id: resolvedListId, label: property.label },
          fieldOptionsId: resolvedListId,
          reviewFieldOptionsId: resolvedListId,
          optionOptionsId: parentOptionId,
          queryKey: listQueryKey(resolvedListId, undefined, 'picker'),
        })
    const optionQueryKey = listQueryKey(resolvedListId, undefined, 'picker')
    const optionMutation: UpsertOptionVariables | undefined = preparedOption
      ? {
          upsertType: 'create',
          input: preparedOption.optionInput,
          queryKey: optionQueryKey,
        }
      : undefined

    if (optionMutation) await executeOptionUpsert(optionMutation)
    if (preparedOption?.review) {
      await submitReviewInteractionSafely({
        review: preparedOption.review,
        mode: 'create',
        message: t('new-option-message', { value: inputValue }),
        mentions: preparedOption.mentions,
        logContext: 'new option',
      })
    }

    const optionAfterCommit = optionMutation
      ? buildOptionAfterCommit({
          optionMutation,
        })
      : undefined

    if (needsReference && referenceID) {
      const referenceListOrganizationID = referenceOrganizationIDFromList({
        ownership,
        fieldOrganizationID: property.organizationID,
      })
      const referencePolicy = resolveCreationReviewPolicy({
        elementType: 'reference',
        source: 'manual',
        ownership,
        listOrganizationID: referenceListOrganizationID,
        isAdmin,
        isSkopAdmin,
        teamReviewWorkflowPolicy,
      })
      const referenceOrganizationID =
        ownership.organizationID || process.env.NEXT_PUBLIC_SKOP_ORGID || ''
      const referenceReviewID = uuid()
      const referenceMutation: CreateRefVariables = {
        isNew: true,
        input: {
          id: referenceID,
          properties: [{ key: field, values: [optionID] }],
          byUserID: ownership.byUserID,
          organizationID:
            ownership.organizationID || process.env.NEXT_PUBLIC_SKOP_ORGID,
          status: referencePolicy.elementStatus,
        },
      }
      const referenceReview = referencePolicy.reviewStatus
        ? (buildCreationReviewInput({
            reviewID: referenceReviewID,
            elementID: referenceID,
            inputValue,
            field,
            reviewType: ReviewType.REVIEW_REFERENCE,
            ownership,
            reviewStatus: referencePolicy.reviewStatus,
            listOrganizationID: referenceListOrganizationID,
            targetOrganizationID: referenceOrganizationID,
          }) as Review)
        : undefined

      await executeReferenceUpsert(referenceMutation)
      if (referenceReview) {
        await submitReviewInteractionSafely({
          mode: 'create',
          review: referenceReview,
          message: t('new-reference-message', { value: inputValue }),
          mentions: buildMentionTargets({ members: byUserID, ownership }),
          logContext: 'new reference',
        })
      }

      return {
        id: referenceID,
        value: referenceID,
        label: inputValue,
        status: referencePolicy.elementStatus,
        target: 'reference',
        nameOption: {
          id: optionID,
          label: existingOption?.label ?? inputValue,
          status:
            existingOption?.status ?? preparedOption?.resolution.elementStatus,
          isNew: !existingOption,
        },
        afterCommit: () => {
          optionAfterCommit?.()
          optimisticallySyncReferenceQueries(queryClient, referenceMutation)
          void invalidateReferenceQueries(queryClient)
        },
        createdReference: {
          id: referenceID,
          byUserID: ownership.byUserID,
          organizationID:
            ownership.organizationID || process.env.NEXT_PUBLIC_SKOP_ORGID,
          status: referencePolicy.elementStatus,
          properties: {
            [field]: [optionID],
          },
        },
      }
    }

    if (!preparedOption) throw new Error('Missing option creation')
    return {
      id: optionID,
      value: preparedOption.optionInput.value ?? inputValue,
      label: inputValue,
      status: preparedOption.resolution.elementStatus,
      target: 'option',
      afterCommit: optionAfterCommit,
    }
  }

  const resubmitOption = async ({
    inputValue,
    option,
    property,
    ownership,
    byUserID,
    parentOptionId,
  }: {
    inputValue: string
    option: EditorOption
    property: EditorProperty
    ownership: Ownership | undefined
    byUserID: LabelWithRole[]
    parentOptionId?: string
  }): Promise<CreateElementResult> => {
    if (!ownership) {
      throw new Error('Missing ownership')
    }
    if (!property.key) {
      throw new Error('Missing property key')
    }

    const resolvedListId =
      typeof property.listId === 'string' && property.listId.length > 0
        ? property.listId
        : typeof property.value === 'string' && property.value.length > 0
          ? property.value
          : property.id
    const targetOrganizationID =
      ownership.organizationID ?? process.env.NEXT_PUBLIC_SKOP_ORGID ?? ''
    const mentions = buildMentionTargets({
      members: byUserID,
      ownership,
    })
    const optionInput = {
      id: option.id,
      label: inputValue,
      value: inputValue,
      status: 'PENDING',
      userID: ownership.byUserID,
      organizationID: targetOrganizationID,
      fieldOptionsId: parentOptionId ? undefined : resolvedListId,
      optionOptionsId: parentOptionId,
    } as const
    const queryKey = listQueryKey(resolvedListId, undefined, 'picker')
    const creationReview = findOptionCreationReview({
      elementId: option.id,
      reviewsById,
    })

    if (!creationReview?.id) {
      throw new Error('Missing creation review for option resubmission')
    }

    await executeOptionUpsert({
      upsertType: 'update',
      input: optionInput,
      queryKey,
    })

    return {
      id: option.id,
      value: option.id,
      label: inputValue,
      status: optionInput.status,
      target: 'option',
      afterCommit: () => {
        syncOptionQueriesAfterCommit({
          upsertType: 'update',
          input: optionInput,
          queryKey,
        })

        void upsertReview({
          upsertType: 'update',
          input: {
            id: creationReview.id,
            status: ReviewStatus.PENDING,
          },
        })

        void submitReviewInteractionSafely({
          review: {
            ...creationReview,
            status: ReviewStatus.PENDING,
          } as Review,
          mode: 'reply',
          message: t('new-option-message', { value: inputValue }),
          mentions,
          logContext: 'resubmitted option',
        })
      },
    }
  }

  return { createElement, resubmitOption }
}

const referenceOrganizationIDFromList = ({
  ownership,
  fieldOrganizationID,
}: {
  ownership: Ownership
  fieldOrganizationID?: string
}) =>
  fieldOrganizationID ||
  ownership.organizationID ||
  process.env.NEXT_PUBLIC_SKOP_ORGID
